import React, { useContext, useEffect, useReducer, useRef } from "react";
import { sortBy } from "lodash";
import analytics from "../../../utils/analytics";
import DeviceInfo from "./DeviceInfo";
import {formatDeviceInfo, setUrl, isNativeApp, isFeatureSwitchEnabled, FEATURE_SWITCHES} from "../../../utils";
import { useCommonContext } from "../CommonProvider";
import { LoadingContext } from "../LoadingProvider";
import { getAllSessions, signOutCurrentDevice, signOutDevice, signOutEverywhere } from "../../../api/custAccountApi";
import { MESSAGE, MESSAGE_TYPE} from "../../../constants/messages";
import InlineAlert from "../../common/InlineAlert";
import { API_URLS } from "../../../constants/urls";
import { getErrorCode } from "../../../utils/errors";
import newRelic from "../../../utils/newrelic";
import { useCustomerAccount } from "../AccountProvider";
import thdCustomer from "@thd-olt-global/thd-customer";
import {
  ButtonGroup,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography
} from "@one-thd/sui-atomic-components";
import {ErrorBoundary} from "@thd-olt-component-react/error-boundary";

// Actions
const FETCH_ALL_SESSION_SUCCESS = "FETCH_ALL_SESSION_SUCCESS";
const SET_STALE = "SET_STALE";
const TOGGLE_MODAL = "TOGGLE_MODAL";
const TOGGLE_INLINE_ALERT = "TOGGLE_INLINE_ALERT";


function reducer(state, action) {
  const newState = {...state};
  switch (action.type) {
    case TOGGLE_MODAL: {
      newState.modalOpen = !state.modalOpen;
      newState.session = action.payload?.session;
      newState.description = action.payload?.description;
      newState.sessions = state.sessions;
      break;
    }

    case FETCH_ALL_SESSION_SUCCESS: {
      newState.sessions = action.payload.sessions;
      newState.deviceStateStale = false;
      break;
    }

    case SET_STALE: {
      newState.deviceStateStale = true;
    } break;

    case TOGGLE_INLINE_ALERT: {
      newState.showInlineAlert = !state.showInlineMsg;
      newState.inlineAlertMsg = action.payload?.inlineAlertMsg;
      newState.inlineAlertType = action.payload?.inlineAlertType;
    } break;
  }

  return newState;
}

const initialState = { modalOpen: false, deviceStateStale: true, signOutModalOpen: false, signOutEverywhereModalOpen: false, showInlineMsg: false, description: ""};

const getDeviceType = (deviceOS) => {
  let deviceType = "Unknown";
  if (deviceOS) {
    const deviceOSLower = deviceOS.toLowerCase();
    if (["iphone/ipad", "ipad", "linux"].includes(deviceOSLower)) {
      deviceType = deviceOS;
    } else if (deviceOSLower.startsWith("windows")) {
      deviceType = "Windows";
    } else if (deviceOSLower.startsWith("ios")) {
      deviceType = "iOS";
    } else if (deviceOSLower.startsWith("android")) {
      deviceType = "Android";
    } else if (deviceOSLower.startsWith("mac")) {
      deviceType = "Mac";
    }
  }
  return deviceType;
};

const DeviceManagement = () => {
  const signOutAllAutomationId = "sign-me-out-section--signout-everywhere-button";
  const [state, dispatch] = useReducer(reducer, initialState);
  const { addAlert } = useCommonContext();
  const { showLoading, hideLoading } = useContext(LoadingContext);
  const deviceSectionRef = useRef(null);
  const { deviceStateStale, showInlineAlert, inlineAlertMsg, inlineAlertType, sessions, modalOpen, description, session } = state;
  const onSignOut = (sessionToSignOut) => {
    const newDescription = sessionToSignOut.current ? MESSAGE.SIGN_OUT_CURRENT_DEVICE : MESSAGE.SIGN_OUT_DEVICE;
    dispatch({ type: TOGGLE_MODAL, payload: { session: sessionToSignOut, description: newDescription } });
  }

  const shouldNotShowDeviceManagement = () => {
    return thdCustomer.customerType !== "B2C" && !isFeatureSwitchEnabled(FEATURE_SWITCHES.ENABLE_B2B_DEVICE_MGMT);
  }

  if(shouldNotShowDeviceManagement()) {
    return null
  }


  const onSignOutEverywhere = () => {
    analytics.postStashEvent("security: sign out everywhere");
    dispatch({ type: TOGGLE_MODAL });
    showLoading();
    signOutEverywhere().then(({response, error}) => {
      hideLoading();
      if(response?.status === 200) {
        setUrl("", `${API_URLS.SIGNIN}?logout=all`);
      } else {
        const errorCode = getErrorCode(error);
        analytics.postErrorEvent("sign out everywhere", errorCode, MESSAGE.SOMETHING_WENT_WRONG);
        addAlert(MESSAGE_TYPE.DANGER, MESSAGE.SOMETHING_WENT_WRONG);
      }
    })
  };

  const onSignOutSpecificDevice = ( deviceInfo) => {
    // eslint-disable-next-line no-shadow
    const { isCurrent, sessionId, deviceOS } = deviceInfo;

    dispatch({ type: TOGGLE_MODAL });
    showLoading();
    const signOutMethod = isCurrent ? signOutCurrentDevice : signOutDevice;

    signOutMethod(sessionId).then(({response, error}) => {
      hideLoading();
      deviceSectionRef.current.scrollIntoView();
      if (response?.status === 200) {
        if (isCurrent) {
          setUrl("", API_URLS.SIGNIN);
        } else {
          dispatch({ type: SET_STALE });
          dispatch({ type: TOGGLE_INLINE_ALERT, payload: { inlineAlertMsg: MESSAGE.DEVICE_SIGNED_OUT, inlineAlertType: MESSAGE_TYPE.SUCCESS}});
        }

        const deviceType = getDeviceType(deviceOS);
        analytics.postSettingsSubmissionEvent(`security:device signed out:${deviceType}`);
        newRelic.logDeviceDeletion(deviceInfo);

      } else {
        const errorCode = getErrorCode(error);
        analytics.postErrorEvent("sign out device", errorCode, MESSAGE.SORRY_SOMETHING_WENT_WRONG);
        dispatch({ type: TOGGLE_INLINE_ALERT, payload: { inlineAlertMsg: MESSAGE.SORRY_SOMETHING_WENT_WRONG, inlineAlertType: MESSAGE_TYPE.DANGER}});
      }
    });
  };

  const signOut = (sessionToSignOut) => {
    if (sessionToSignOut) {
      onSignOutSpecificDevice(sessionToSignOut);
    } else {
      onSignOutEverywhere();
    }
  };

  const { stale } = useCustomerAccount();
  useEffect( () => {
    if(deviceStateStale || stale) {
      showLoading(true);
      getAllSessions().then( ({response}) => {
      // getAllSessions().then( ({response, error}) => {
        hideLoading();
        if(response?.data) {
          const sortedResponse =  sortBy(response.data, [ (device) => device.signInTS]).reverse(); // sort by timestamp and current status to get recent device sessions
          const formattedDevices = formatDeviceInfo(sortedResponse.slice(0,6)); // returns recent 6 device sessions
          let currentDevice = null;

          formattedDevices.forEach( (device, index) => {
            if(device.current) {
              currentDevice = formattedDevices.splice(index,1);
            }
          });

          if(currentDevice){
            formattedDevices.unshift(currentDevice[0]);
          }

          dispatch({type: FETCH_ALL_SESSION_SUCCESS, payload: {sessions: formattedDevices}})
        } else {
          dispatch({ type: TOGGLE_INLINE_ALERT, payload: { inlineAlertMsg: MESSAGE.CANT_SHOW_DEVICES, inlineAlertType: MESSAGE_TYPE.DANGER}});
        }
      })
    }
  }, [deviceStateStale, stale]);

  return (
      <ErrorBoundary name="device-management">
    <section ref={deviceSectionRef} className="col__12-12 u--paddingNone u--paddingNormal-top">
      <Typography weight="display" variant="h2" uppercase>
        Where You&apos;re Signed In
      </Typography>
      {!isNativeApp() &&
        <div className="security-info-row u--paddingNormal-bottom u--padding-top">
          <div>
            <span className="security-info-row--action">
              <button className="u__default-link"
                type="button"
                data-automation-id={signOutAllAutomationId}
                onClick={() => { dispatch({ type: TOGGLE_MODAL, payload: { description: MESSAGE.SIGN_OUT_EVERYWHERE } }) }}><Link>Sign Out Everywhere</Link></button>
            </span>
          </div>
        </div> }

      <div className="all-device-info">
        {showInlineAlert && <InlineAlert alertMessage={inlineAlertMsg} alertType={inlineAlertType}/>}
        <DeviceInfo sessions={sessions} onSignOut={onSignOut} isNativeApp={isNativeApp}/>
      </div>

      <Modal
        open = {modalOpen}
        onClose={() => { dispatch({ type: TOGGLE_MODAL }) }}
      >
        <ModalHeader>
          Sign Out
        </ModalHeader>
        <ModalBody>
          {description}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup orientation='vertical'>
            <Button onClick={() => signOut(session)} data-automation-id="signOutDevice" variant='primary'>Yes, Sign Me Out</Button>
            <Button onClick={() => { dispatch({ type: TOGGLE_MODAL }) }} data-automation-id="cancel">Cancel</Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

    </section>
      </ErrorBoundary>
  )
}

export default DeviceManagement;